// Tiny DOM helpers. Vanilla, no framework.

export type Attrs = Partial<{
  class: string;
  id: string;
  title: string;
  type: string;
  value: string;
  placeholder: string;
  href: string;
  role: string;
  tabindex: number | string;
  disabled: boolean;
  checked: boolean;
  min: number | string;
  max: number | string;
  step: number | string;
  for: string;
  textContent: string;
  innerHTML: string;
  ariaLabel: string;
  ariaPressed: 'true' | 'false';
  ariaCurrent: string;
  ariaSelected: 'true' | 'false';
  ariaExpanded: 'true' | 'false';
  draggable: boolean;
  dataset: Record<string, string>;
  style: Partial<CSSStyleDeclaration> | string;
}>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs,
  children?: (Node | string | null | undefined | false)[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) applyAttrs(node, attrs);
  if (children) {
    for (const c of children) {
      if (c === null || c === undefined || c === false) continue;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
  }
  return node;
}

function applyAttrs(node: HTMLElement, attrs: Attrs): void {
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'textContent') node.textContent = String(v);
    else if (k === 'innerHTML') node.innerHTML = String(v);
    else if (k === 'ariaLabel') node.setAttribute('aria-label', String(v));
    else if (k === 'ariaPressed') node.setAttribute('aria-pressed', String(v));
    else if (k === 'ariaSelected') node.setAttribute('aria-selected', String(v));
    else if (k === 'ariaCurrent') node.setAttribute('aria-current', String(v));
    else if (k === 'ariaExpanded') node.setAttribute('aria-expanded', String(v));
    else if (k === 'tabindex') node.setAttribute('tabindex', String(v));
    else if (k === 'role') node.setAttribute('role', String(v));
    else if (k === 'for') node.setAttribute('for', String(v));
    else if (k === 'draggable') node.setAttribute('draggable', v ? 'true' : 'false');
    else if (k === 'disabled') {
      if (v) node.setAttribute('disabled', '');
    } else if (k === 'checked') {
      if (v) (node as HTMLInputElement).checked = true;
    } else if (k === 'dataset' && typeof v === 'object') {
      for (const [dk, dv] of Object.entries(v as Record<string, string>)) {
        (node.dataset as DOMStringMap)[dk] = dv;
      }
    } else if (k === 'style') {
      if (typeof v === 'string') node.setAttribute('style', v);
      else Object.assign(node.style, v as Partial<CSSStyleDeclaration>);
    } else if (k === 'value') {
      (node as HTMLInputElement).value = String(v);
    } else {
      node.setAttribute(k, String(v));
    }
  }
}

export function on<K extends keyof HTMLElementEventMap>(
  target: EventTarget,
  type: K,
  handler: (ev: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on(
  target: EventTarget,
  type: string,
  handler: (ev: Event) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on(
  target: EventTarget,
  type: string,
  handler: (ev: Event) => void,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler, options);
  return () => target.removeEventListener(type, handler, options);
}

/** Replace contents with new children. */
export function replaceChildren(parent: HTMLElement, ...children: Node[]): void {
  parent.replaceChildren(...children);
}

/** Run fn on the next animation frame, coalescing calls. */
export function raf(fn: () => void): () => void {
  let scheduled = false;
  return () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      fn();
    });
  };
}

/** Format a number with up to N significant decimal places, no trailing zeros. */
export function formatNum(n: number, places = 2): string {
  if (Number.isInteger(n)) return n.toString();
  return parseFloat(n.toFixed(places)).toString();
}
